import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';

import { GET_ONE, UPDATE, history, notify, provider } from '../_utils';
import Utils from './components/utils';
import { deptLabel, formatReportPeriod, fullName, placeLabel, typeLabel } from './format';
import { applyPayrollBonus, formatHours, formatMoney, formatOcc, hoursFromSeconds, motivationLabel, roundMoney } from './motivation/formula';

const { reportsStatus, contractTypes, parttimeTypes } = Utils.options();

const LOCKED_STATUSES = ['Approved', 'Accepted'];

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const Row = ({ label, value, mono }) => (
    <div className="r">
        <span className="k">{label}</span>
        <span className={`v${mono ? ' m' : ''}${value == null || value === '' || value === '—' ? ' empty' : ''}`}>
            {value == null || value === '' ? '—' : value}
        </span>
    </div>
);

const seconds = (value) => (value == null ? '—' : formatHours(hoursFromSeconds(value)));

const PaySheet = ({ id, onSaved }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [calc, setCalc] = useState(null);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        const get = async () => {
            setLoading(true);
            const { data: payload, status } = await provider(GET_ONE, '/operators/reports', id);
            if (status === 200) {
                setData(payload);
                setCalc(payload.calculation || null);
                setDirty(false);
            }
            setLoading(false);
        };
        get();
    }, [id]);

    const close = () => history.push('/operators/reports');
    const locked = LOCKED_STATUSES.includes(data?.status);

    const changeBonus = (patch) => {
        if (!calc || locked) return;
        setCalc(applyPayrollBonus(calc, patch));
        setDirty(true);
    };

    const save = async () => {
        if (!calc || !dirty) return;
        setSaving(true);
        const { status, message } = await provider(UPDATE, '/operators/reports', {
            id,
            data: {
                calculation: calc,
                summa: calc.toPay,
                premia: calc.bonusPay,
            },
        });
        setSaving(false);
        if (status === 200) {
            notify({ message: 'Премия сохранена', type: 'success' });
            setDirty(false);
            setData((prev) => ({ ...prev, calculation: calc, summa: calc.toPay, premia: calc.bonusPay }));
            if (onSaved) onSaved();
        } else {
            notify({ message: message || 'Не удалось сохранить премию', type: 'error' });
        }
    };

    const operator = data?.operator || {};
    const version = data?.motivation || data?.motivationVersion || calc?.motivation;
    const statistic = data?.statistic || {};
    const detail = data?.detail || {};
    const statusName = reportsStatus.find((item) => item.id === data?.status)?.name || data?.status || '—';
    const shown = calc || data?.calculation;
    const payoutKind = shown?.payoutKind || (shown?.contractType === 'Contract' || operator.contract?.type === 'Contract' ? 'contract' : 'scheme');
    const isContractPay = payoutKind === 'contract';
    const sheetContractType = shown?.contractType || operator.contract?.type || data?.contractType;
    const contractName = contractTypes.find((item) => item.id === sheetContractType)?.name || sheetContractType || '—';
    const parttimeId = shown?.parttime || operator.contract?.parttime;
    const parttimeName = parttimeTypes.find((item) => item.id === parttimeId)?.name || parttimeId || '';
    const displayToPay = isContractPay
        ? roundMoney((Number(shown?.contractPay) || 0) + (Number(shown?.bonusPay) || 0))
        : (shown?.toPay ?? data?.summa);

    return (
        <div className="pc-emp-ov" onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
            <div className="pc-emp-modal pc-emp-modal--lg" role="dialog" aria-modal="true">
                <div className="pc-emp-mh">
                    <div>
                        <h3>{loading ? 'Расчётный лист' : `${operator.login || data?.login || ''} ${fullName(operator) || fullName(data) || ''}`.trim() || 'Расчётный лист'}</h3>
                        <div className="sub">
                            {[formatReportPeriod(data?.period), motivationLabel(version)].filter(Boolean).join(' · ') || '—'}
                        </div>
                    </div>
                    <button type="button" className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet" onClick={close} title="Закрыть">
                        <Icon d="M4 4l8 8M12 4L4 12" />
                    </button>
                </div>
                <div className="pc-emp-mb">
                    {loading ? (
                        <div className="pc-emp-empty"><p>Загрузка…</p></div>
                    ) : !data ? (
                        <div className="pc-emp-empty"><b>Не найдено</b><p>Расчётный лист недоступен.</p></div>
                    ) : (
                        <Fragment>
                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Сотрудник</div>
                                <div className="pc-emp-dl">
                                    <Row label="Тип" value={typeLabel(operator.type || data.type)} />
                                    <Row label="Отдел" value={deptLabel(operator.department || data.department)} />
                                    <Row label="Площадка" value={placeLabel(operator.place || data.place)} />
                                    <Row label="Договор" value={contractName} />
                                    {parttimeName ? <Row label="Ставка" value={parttimeName} /> : null}
                                    <Row label="Статус листа" value={statusName} />
                                </div>
                            </div>

                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Версия мотивации</div>
                                {version ? (
                                    <div className="pc-emp-dl">
                                        <Row label="Версия" value={motivationLabel(version)} mono />
                                        <Row label="Создана" value={version.createdAt ? moment(version.createdAt).format('DD.MM.YYYY HH:mm') : '—'} />
                                        <Row label="Комментарий" value={typeof version.comment === 'string' ? version.comment : '—'} />
                                    </div>
                                ) : (
                                    <div className="pc-emp-note">У этого расчёта нет привязанной версии мотивации — лист создан по старой схеме.</div>
                                )}
                            </div>

                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Статистика периода</div>
                                <div className="pc-emp-dl">
                                    <Row label="Общее время" value={seconds(statistic.totalTime || data.totalTime)} mono />
                                    <Row label="В линии" value={seconds(statistic.paid || data.paidTime)} mono />
                                    <Row label="В разговоре" value={seconds(statistic.talkTime || data.talkTime)} mono />
                                    <Row label="OCC" value={formatOcc(statistic.occPercent ?? data.occPercent)} />
                                    <Row label="Звонков" value={statistic.calls != null ? String(statistic.calls) : '—'} />
                                </div>
                            </div>

                            {shown ? (
                                <div className="pc-emp-card">
                                    <div className="ch"><h3>{isContractPay ? 'Расчёт по контракту' : 'Расчёт по системе мотивации'}</h3></div>
                                    <div className="cb">
                                        {isContractPay ? (
                                            <div className="pc-emp-dl">
                                                <Row label="Оплачиваемые часы" value={formatHours(shown.hoursPaid)} mono />
                                                {shown.normFullHours != null ? <Row label="Норма месяца" value={formatHours(shown.normFullHours)} mono /> : null}
                                                {shown.normHours != null ? <Row label="Личная норма" value={formatHours(shown.normHours)} mono /> : null}
                                                {shown.salaryApplied != null ? <Row label="Оклад" value={formatMoney(shown.salaryApplied)} mono /> : null}
                                            </div>
                                        ) : (
                                            <div className="pc-emp-dl">
                                                <Row label="Часы перерасчёт" value={formatHours(shown.hoursRecalc)} mono />
                                                <Row label="База" value={formatMoney(shown.basePay)} mono />
                                            </div>
                                        )}
                                        <div className="pc-emp-fg" style={{ marginTop: 12 }}>
                                            <label className="pc-emp-fl">
                                                <span>Премия, %</span>
                                                <input
                                                    className="pc-emp-inp pc-emp-inp--mono"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    disabled={locked}
                                                    value={shown.bonusPercent ?? ''}
                                                    onChange={(e) => changeBonus({ percent: e.target.value })}
                                                />
                                            </label>
                                            <label className="pc-emp-fl">
                                                <span>Премия, р</span>
                                                <input
                                                    className="pc-emp-inp pc-emp-inp--mono"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    disabled={locked}
                                                    value={shown.bonusPay ?? ''}
                                                    onChange={(e) => changeBonus({ amount: e.target.value })}
                                                />
                                            </label>
                                        </div>
                                        {locked ? (
                                            <div className="pc-emp-note" style={{ marginTop: 10 }}>Лист утверждён или подписан — премию менять нельзя.</div>
                                        ) : (
                                            <p className="pc-mot-hint">
                                                {isContractPay
                                                    ? 'Премия добавляется к окладу по норме. К выплате = оклад (по часам) + премия.'
                                                    : 'Процент пересчитывает сумму от базы, сумма пересчитывает процент. Итог схемы и «к выплате» обновляются сразу.'}
                                            </p>
                                        )}
                                        <div className="pc-emp-dl" style={{ marginTop: 12 }}>
                                            {isContractPay ? (
                                                <Fragment>
                                                    <Row label="Оклад по часам" value={formatMoney(shown.contractPay)} mono />
                                                    <Row label="К выплате" value={formatMoney(displayToPay)} mono />
                                                </Fragment>
                                            ) : (
                                                <Fragment>
                                                    <Row label="Надбавки" value={formatMoney(shown.allowance || 0)} />
                                                    <Row label="Итого схема" value={formatMoney(shown.schemeTotal)} mono />
                                                    <Row label="К выплате" value={formatMoney(shown.toPay ?? data.summa)} mono />
                                                </Fragment>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : detail.paidTime ? (
                                <div className="pc-emp-card">
                                    <div className="ch"><h3>Старый расчёт</h3></div>
                                    <div className="cb">
                                        <div className="pc-emp-dl">
                                            <Row
                                                label="В линии"
                                                value={`${Number(detail.paidTime.time || 0).toFixed(2)} мин × ${detail.paidTime.tarif} = ${formatMoney(detail.paidTime.paid)}`}
                                            />
                                            <Row
                                                label="В разговоре"
                                                value={`${Number(detail.talkTime?.time || 0).toFixed(2)} мин × ${detail.talkTime?.tarif} = ${formatMoney(detail.talkTime?.paid)}`}
                                            />
                                            <Row label="% премии" value={`${detail.premia?.percent || 0}%`} />
                                            <Row label="Премиальные" value={formatMoney(detail.premia?.paid)} />
                                            <Row label="Итого" value={formatMoney(data.summa)} mono />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="pc-emp-note">Детализация расчёта ещё не заполнена. Запустите расчёт по кнопке «Рассчитать ЗП».</div>
                            )}
                        </Fragment>
                    )}
                </div>
                <div className="pc-emp-mf">
                    {shown && !locked ? (
                        <button type="button" className="pc-emp-btn pc-emp-btn--pri" disabled={!dirty || saving} onClick={save}>
                            {saving ? 'Сохраняем…' : 'Сохранить премию'}
                        </button>
                    ) : null}
                    <button type="button" className="pc-emp-btn" onClick={close}>Закрыть</button>
                </div>
            </div>
        </div>
    );
};

export default PaySheet;
